// ./src/components/features/WorkflowItemTable/WorkflowItemEditDrawer.tsx
import React, { useEffect, useState } from "react";
import {
  Stack,
  TextInput,
  Textarea,
  Select,
  Switch,
  Divider,
} from "@mantine/core";
import { OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";
import { WorkflowItem, WorkflowItemInput } from "@/utils/api";
import { AppDrawer, StatusSelector } from "@/components/generic";
import { ConnectorSelector } from "@/components/features/Connectors/ConnectorSelector";
import { AccountEmailSelector } from "@/components/features/AccountEmails/AccountEmailSelector";
import { FILE_STORAGE_CONNECTOR_TYPES } from "@/config";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Field names exactly as the API sends/receives them.
// Used to extract field-level errors from OpResult via getFormErrors().
// orderNo is intentionally excluded — ordering will be handled via drag-and-drop.
const FORM_FIELDS = [
  "conditionType",
  "formFieldName",
  "compareOperation",
  "fieldValue",
  "actionType",
  "connectorId",
  "dataConnectorId",
  "accountEmailId",
  "submissionStatus",
  "rejectReason",
  "stopOnConditionMet",
  "status",
] as const;

// ---------------------------------------------------------------------------
// Condition type options
// CUSTOM is excluded per product decision — handled separately if needed.
// ---------------------------------------------------------------------------
const CONDITION_TYPE_OPTIONS = [
  { value: "ALWAYS", label: "Always" },
  { value: "ON_FIELD_EMPTY", label: "Field is empty" },
  { value: "ON_FIELD_NOT_EMPTY", label: "Field is not empty" },
  { value: "ON_FIELD_VALUE_COMPARE", label: "Field value comparison" },
] as const;

type ConditionType = (typeof CONDITION_TYPE_OPTIONS)[number]["value"];

// Condition types that require a field name
const CONDITION_NEEDS_FIELD: ConditionType[] = [
  "ON_FIELD_EMPTY",
  "ON_FIELD_NOT_EMPTY",
  "ON_FIELD_VALUE_COMPARE",
];

// ---------------------------------------------------------------------------
// Compare operation options — shown only for ON_FIELD_VALUE_COMPARE
// ---------------------------------------------------------------------------
const COMPARE_OPERATION_OPTIONS = [
  { value: "==", label: "Equals" },
  { value: "!=", label: "Does not equal" },
  { value: "<", label: "Less than" },
  { value: "<=", label: "Less than or equal" },
  { value: ">", label: "Greater than" },
  { value: ">=", label: "Greater than or equal" },
  { value: "contain", label: "Contains" },
  { value: "not_contain", label: "Does not contain" },
  { value: "starts_with", label: "Starts with" },
  { value: "ends_with", label: "Ends with" },
  { value: "regex", label: "Matches regular expression" },
] as const;

// ---------------------------------------------------------------------------
// Action type options
// TRANSFORM is excluded per product decision — handled separately if needed.
// ---------------------------------------------------------------------------
const ACTION_TYPE_OPTIONS = [
  { value: "NOTIFY", label: "Notify (send to connector / email)" },
  { value: "REJECT", label: "Reject submission" },
] as const;

// ---------------------------------------------------------------------------
// Submission status options
// Values sourced from SUBMISSION_STATUSES used by SubmissionStatusChanger.
// Stored as strings in the Select, converted to number on submit.
// ---------------------------------------------------------------------------
const SUBMISSION_STATUS_OPTIONS = [
  { value: "", label: "No status change" },
  { value: "10", label: "Spam" },
  { value: "100", label: "Received" },
  { value: "200", label: "Reviewed" },
  { value: "300", label: "Accepted" },
  { value: "400", label: "Declined" },
];

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface FormValues {
  conditionType: string;
  formFieldName: string;
  compareOperation: string;
  fieldValue: string;
  actionType: string;
  // Selectors return string | null; stored as string | null throughout
  connectorId: string | null;
  dataConnectorId: string | null;
  accountEmailId: string | null;
  // Stored as a string for the Select; converted to number | undefined on submit
  submissionStatus: string;
  rejectReason: string;
  stopOnConditionMet: boolean;
  status: number | null;
}

const EMPTY_FORM: FormValues = {
  conditionType: "ALWAYS",
  formFieldName: "",
  compareOperation: "",
  fieldValue: "",
  actionType: "NOTIFY",
  connectorId: null,
  dataConnectorId: null,
  accountEmailId: null,
  submissionStatus: "",
  rejectReason: "",
  stopOnConditionMet: false,
  status: STATUSES.ACTIVE,
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface WorkflowItemEditDrawerProps {
  /** Whether the drawer is open */
  opened: boolean;

  /** Called when the drawer should close */
  onClose: () => void;

  /**
   * Workflow item to edit. When null/undefined the drawer is in create mode.
   * Re-populates the form when it changes while the drawer is already open.
   */
  workflowItem?: WorkflowItem | null;

  /**
   * workflowId to pre-fill on create. The page passes this so the item
   * is always linked to the correct workflow without the user selecting it.
   */
  workflowId?: string | null;

  /** Called when the user submits a new workflow item */
  onCreateWorkflowItem: (params: WorkflowItemInput) => Promise<OpResult>;

  /** Called when the user submits changes to an existing workflow item */
  onUpdateWorkflowItem: (
    id: string,
    params: WorkflowItemInput,
  ) => Promise<OpResult>;

  /** True while a create call is in flight */
  isCreating: boolean;

  /** True while an update call is in flight */
  isUpdating: boolean;

  /**
   * Last OpResult from a failed create or update.
   * Field-level errors shown under each input; generic errors shown above footer.
   * No client-side validation is performed.
   */
  lastError: OpResult | null;

  /** Clears lastError — called on close and when user edits any field */
  onClearError: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns true when the given conditionType requires a formFieldName */
const needsFieldName = (ct: string): boolean =>
  CONDITION_NEEDS_FIELD.includes(ct as ConditionType);

/** Returns true when compareOperation and fieldValue are needed */
const needsComparison = (ct: string): boolean =>
  ct === "ON_FIELD_VALUE_COMPARE";

/** Returns true when connector / email fields are shown */
const isNotifyAction = (at: string): boolean => at === "NOTIFY";

/** Returns true when rejectReason is shown */
const isRejectAction = (at: string): boolean => at === "REJECT";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const WorkflowItemEditDrawer: React.FC<WorkflowItemEditDrawerProps> = ({
  opened,
  onClose,
  workflowItem,
  workflowId,
  onCreateWorkflowItem,
  onUpdateWorkflowItem,
  isCreating,
  isUpdating,
  lastError,
  onClearError,
}) => {
  const isEditMode = !!workflowItem?.id;
  const isBusy = isCreating || isUpdating;

  // ------------------------------------------------------------------
  // Form state
  // ------------------------------------------------------------------
  const [formValues, setFormValues] = useState<FormValues>(EMPTY_FORM);

  // ------------------------------------------------------------------
  // Populate / reset on open or when the item prop changes
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!opened) {
      return;
    }

    if (workflowItem) {
      setFormValues({
        conditionType: workflowItem.conditionType ?? "ALWAYS",
        formFieldName: workflowItem.formFieldName ?? "",
        compareOperation: workflowItem.compareOperation ?? "",
        fieldValue: workflowItem.fieldValue ?? "",
        actionType: workflowItem.actionType ?? "NOTIFY",
        connectorId: workflowItem.connectorId ?? null,
        dataConnectorId: workflowItem.dataConnectorId ?? null,
        accountEmailId: workflowItem.accountEmailId ?? null,
        // submissionStatus stored as string for Select; empty string = "no change"
        submissionStatus:
          workflowItem.submissionStatus != null
            ? String(workflowItem.submissionStatus)
            : "",
        rejectReason: workflowItem.rejectReason ?? "",
        stopOnConditionMet: workflowItem.stopOnConditionMet ?? false,
        status: workflowItem.status ?? STATUSES.ACTIVE,
      });
    } else {
      setFormValues(EMPTY_FORM);
    }

    onClearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, workflowItem]);

  // ------------------------------------------------------------------
  // Error extraction
  // ------------------------------------------------------------------
  const { fields: fieldErrors, genericErrors } = OpResult.getFormErrors(
    lastError,
    FORM_FIELDS as unknown as string[],
  );

  // ------------------------------------------------------------------
  // Generic field change — clears errors on first edit
  // ------------------------------------------------------------------
  const handleChange = <K extends keyof FormValues>(
    field: K,
    value: FormValues[K],
  ) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    if (lastError) {
      onClearError();
    }
  };

  // ------------------------------------------------------------------
  // conditionType change — clears fields belonging to the previous type
  // so stale values are never submitted to the API
  // ------------------------------------------------------------------
  const handleConditionTypeChange = (value: string | null) => {
    const ct = value ?? "ALWAYS";
    setFormValues((prev) => ({
      ...prev,
      conditionType: ct,
      formFieldName: needsFieldName(ct) ? prev.formFieldName : "",
      compareOperation: needsComparison(ct) ? prev.compareOperation : "",
      fieldValue: needsComparison(ct) ? prev.fieldValue : "",
    }));
    if (lastError) {
      onClearError();
    }
  };

  // ------------------------------------------------------------------
  // actionType change — clears fields belonging to the previous type
  // submissionStatus is shared between NOTIFY and REJECT — intentionally kept
  // ------------------------------------------------------------------
  const handleActionTypeChange = (value: string | null) => {
    const at = value ?? "NOTIFY";
    setFormValues((prev) => ({
      ...prev,
      actionType: at,
      connectorId: isNotifyAction(at) ? prev.connectorId : null,
      dataConnectorId: isNotifyAction(at) ? prev.dataConnectorId : null,
      accountEmailId: isNotifyAction(at) ? prev.accountEmailId : null,
      rejectReason: isRejectAction(at) ? prev.rejectReason : "",
    }));
    if (lastError) {
      onClearError();
    }
  };

  // ------------------------------------------------------------------
  // Submit — build a clean payload, omitting fields that don't apply
  // to the selected condition/action type
  // ------------------------------------------------------------------
  const handleSubmit = async () => {
    const params: WorkflowItemInput = {
      // workflowId: on create comes from the prop; on edit re-sent for safety
      workflowId: workflowItem?.workflowId ?? workflowId ?? undefined,

      conditionType: formValues.conditionType || undefined,
      formFieldName: needsFieldName(formValues.conditionType)
        ? formValues.formFieldName.trim() || undefined
        : undefined,
      compareOperation: needsComparison(formValues.conditionType)
        ? formValues.compareOperation || undefined
        : undefined,
      fieldValue: needsComparison(formValues.conditionType)
        ? formValues.fieldValue.trim() || undefined
        : undefined,

      actionType: formValues.actionType || undefined,
      connectorId: isNotifyAction(formValues.actionType)
        ? (formValues.connectorId ?? undefined)
        : undefined,
      dataConnectorId: isNotifyAction(formValues.actionType)
        ? (formValues.dataConnectorId ?? undefined)
        : undefined,
      accountEmailId: isNotifyAction(formValues.actionType)
        ? (formValues.accountEmailId ?? undefined)
        : undefined,

      // submissionStatus: convert non-empty string back to number;
      // empty string means "no status change" — send undefined
      submissionStatus:
        formValues.submissionStatus !== ""
          ? Number(formValues.submissionStatus)
          : undefined,

      rejectReason: isRejectAction(formValues.actionType)
        ? formValues.rejectReason.trim() || undefined
        : undefined,

      stopOnConditionMet: formValues.stopOnConditionMet,
      status: formValues.status ?? undefined,
    };

    if (isEditMode && workflowItem?.id) {
      await onUpdateWorkflowItem(workflowItem.id, params);
    } else {
      await onCreateWorkflowItem(params);
    }
    // Parent decides whether to close based on result.didSucceed()
  };

  const handleClose = () => {
    onClearError();
    onClose();
  };

  // ------------------------------------------------------------------
  // Derived visibility flags — computed once per render
  // ------------------------------------------------------------------
  const showFieldName = needsFieldName(formValues.conditionType);
  const showComparison = needsComparison(formValues.conditionType);
  const showNotifyFields = isNotifyAction(formValues.actionType);
  const showRejectFields = isRejectAction(formValues.actionType);
  const showSubmissionStatus = showNotifyFields || showRejectFields;

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <AppDrawer
      opened={opened}
      onClose={handleClose}
      onConfirm={handleSubmit}
      title={isEditMode ? "Edit Workflow Item" : "New Workflow Item"}
      actionType="save-cancel"
      size="lg"
      position="right"
      closable={!isBusy}
      loading={isBusy}
      errors={genericErrors.length > 0 ? genericErrors : undefined}
    >
      <Stack gap="md">
        {/* ----------------------------------------------------------------
            General — status applies regardless of condition / action type
        ---------------------------------------------------------------- */}
        <StatusSelector
          label="Status"
          value={formValues.status}
          onChange={(value) => handleChange("status", value)}
          error={fieldErrors.status}
          disabled={isBusy}
          withIcons
        />

        <Divider label="Condition" labelPosition="left" />

        {/* ----------------------------------------------------------------
            Condition section
        ---------------------------------------------------------------- */}
        <Stack gap="sm">
          <Select
            label="Condition type"
            description="When should this workflow item fire?"
            data={
              CONDITION_TYPE_OPTIONS as unknown as {
                value: string;
                label: string;
              }[]
            }
            value={formValues.conditionType}
            onChange={handleConditionTypeChange}
            error={fieldErrors.conditionType}
            disabled={isBusy}
            allowDeselect={false}
          />

          {showFieldName && (
            <TextInput
              label="Form field name"
              description="The exact name of the field to check"
              placeholder="e.g. email"
              value={formValues.formFieldName}
              onChange={(e) =>
                handleChange("formFieldName", e.currentTarget.value)
              }
              error={fieldErrors.formFieldName}
              disabled={isBusy}
            />
          )}

          {showComparison && (
            <>
              <Select
                label="Compare operation"
                data={
                  COMPARE_OPERATION_OPTIONS as unknown as {
                    value: string;
                    label: string;
                  }[]
                }
                value={formValues.compareOperation}
                onChange={(value) =>
                  handleChange("compareOperation", value ?? "")
                }
                error={fieldErrors.compareOperation}
                disabled={isBusy}
                allowDeselect={false}
              />

              <TextInput
                label="Compare value"
                description="The value to compare the field against"
                placeholder="e.g. yes"
                value={formValues.fieldValue}
                onChange={(e) =>
                  handleChange("fieldValue", e.currentTarget.value)
                }
                error={fieldErrors.fieldValue}
                disabled={isBusy}
              />
            </>
          )}

          <Switch
            label="Stop on condition met"
            description="Do not evaluate further workflow items if this condition is satisfied"
            checked={formValues.stopOnConditionMet}
            onChange={(e) =>
              handleChange("stopOnConditionMet", e.currentTarget.checked)
            }
            disabled={isBusy}
          />
        </Stack>

        <Divider label="Action" labelPosition="left" />

        {/* ----------------------------------------------------------------
            Action section
        ---------------------------------------------------------------- */}
        <Stack gap="sm">
          <Select
            label="Action type"
            description="What should happen when the condition is met?"
            data={
              ACTION_TYPE_OPTIONS as unknown as {
                value: string;
                label: string;
              }[]
            }
            value={formValues.actionType}
            onChange={handleActionTypeChange}
            error={fieldErrors.actionType}
            disabled={isBusy}
            allowDeselect={false}
          />

          {showNotifyFields && (
            <>
              <ConnectorSelector
                label="Data connector"
                description="Send form submission data (excluding files) to this connector"
                value={formValues.connectorId}
                onChange={(connectorId) =>
                  handleChange("connectorId", connectorId)
                }
                placeholder="Select a data connector"
                error={fieldErrors.connectorId}
                disabled={isBusy}
              />

              <ConnectorSelector
                label="Files connector"
                description="Send uploaded files to this connector"
                value={formValues.dataConnectorId}
                onChange={(dataConnectorId) =>
                  handleChange("dataConnectorId", dataConnectorId)
                }
                placeholder="Select a files connector"
                types={FILE_STORAGE_CONNECTOR_TYPES}
                error={fieldErrors.dataConnectorId}
                disabled={isBusy}
              />

              <AccountEmailSelector
                label="Account email"
                description="Send notification via this email account"
                value={formValues.accountEmailId}
                onChange={(accountEmailId) =>
                  handleChange("accountEmailId", accountEmailId)
                }
                placeholder="Select an email account"
                confirmedOnly
                error={fieldErrors.accountEmailId}
                disabled={isBusy}
              />
            </>
          )}

          {showSubmissionStatus && (
            <Select
              label="Submission status"
              description="Assign this status to the submission when the action fires"
              data={SUBMISSION_STATUS_OPTIONS}
              value={formValues.submissionStatus}
              onChange={(value) =>
                handleChange("submissionStatus", value ?? "")
              }
              error={fieldErrors.submissionStatus}
              disabled={isBusy}
              allowDeselect={false}
            />
          )}

          {showRejectFields && (
            <Textarea
              label="Reject reason"
              description="Message shown to the submitter explaining why their submission was not accepted"
              placeholder="Your submission was not accepted because..."
              value={formValues.rejectReason}
              onChange={(e) =>
                handleChange("rejectReason", e.currentTarget.value)
              }
              error={fieldErrors.rejectReason}
              disabled={isBusy}
              autosize
              minRows={3}
              maxRows={6}
            />
          )}
        </Stack>
      </Stack>
    </AppDrawer>
  );
};
